import React from 'react'
import { LockIcon, TriangleAlertIcon } from 'lucide-react'

import { Button } from '@repo/ui'
import { EmptyState } from 'components/display/EmptyState/EmptyState'
import { isForbidden } from 'services'
import { useVeraSpaceStore } from 'stores'
import { spaceAccessDeniedText } from 'utils/veraSupportAccess'

interface QueryErrorStateProps {
  /** The react-query error. */
  error: unknown
  /** What could not be loaded, lowercase, e.g. "support requests". */
  subject: string
  /** Named in the access-denied copy so the user can ask for the right thing. */
  permissionLabel?: string
  onRetry?: () => void
}

/**
 * What to render when a list query FAILED, as distinct from returning nothing.
 *
 * This exists because of a real defect (VRG-226): `/internal/support` rendered
 * "Nothing in the queue — requests from every company land here as they come in"
 * while the API was returning 403 and requests did in fact exist. The page read
 * `const { data = [] } = useQuery(...)`, so a failure and an empty result were
 * indistinguishable, and the friendlier of the two messages won.
 *
 * That failure mode is worth naming, because the `data = []` default is
 * idiomatic and appears all over this codebase: **an authorization failure
 * presented as an empty dataset is not a cosmetic bug.** It tells an operator a
 * true-looking falsehood — "there is no work here" — and there is nothing on
 * screen to suggest otherwise, so they have no reason to doubt it and no thread
 * to pull. A spinner that never resolves is a better outcome than this, because
 * at least it looks broken.
 *
 * So: any surface that renders a list from a query must handle three states, not
 * two — loading, failed, and genuinely empty. This component is the middle one,
 * and it splits again by cause: 403 gets access-denied copy naming the missing
 * permission (actionable — go ask an admin), anything else gets a load failure
 * with a retry (also actionable, differently). Collapsing those two would
 * reintroduce a smaller version of the same lie.
 *
 * VRG-677: the 403 splits once more, by WHO is reading. Inside a brand's space the
 * reader is Vera support, capped to the brand's grant, and "ask an admin" would send
 * them to their own (Vera) admin, who cannot fix it. So in a space the copy names the
 * brand's grant and where the brand changes it. One component owns this so every page
 * that renders a failed query says the same thing (§28/§36).
 */
export const QueryErrorState = ({
  error,
  subject,
  permissionLabel,
  onRetry,
}: QueryErrorStateProps) => {
  const inSpace = !!useVeraSpaceStore((s) => s.companyId)

  if (isForbidden(error) && inSpace) {
    return (
      <EmptyState
        icon={<LockIcon strokeWidth={1} />}
        title="Vera support can't see this"
        text={spaceAccessDeniedText({ subject, permissionLabel })}
      />
    )
  }

  if (isForbidden(error)) {
    return (
      <EmptyState
        icon={<LockIcon strokeWidth={1} />}
        title="You don't have access to this"
        text={
          permissionLabel
            ? `Viewing ${subject} needs the '${permissionLabel}' permission. Ask an admin to grant it.`
            : `You don't have permission to view ${subject}. Ask an admin for access.`
        }
      />
    )
  }

  return (
    <EmptyState
      icon={<TriangleAlertIcon strokeWidth={1} />}
      title={`Couldn't load ${subject}`}
      /*
        The body is for a message that ADDS something — a server's explanation of what
        went wrong. It is not for repeating the title.

        Every `services/*Api.ts` call passes `ok()` a client-side fallback phrased as a
        full sentence ("Couldn't load What's new"), because a toast needs one. When the
        request never reached the server — the API down, a network drop — that fallback
        is what lands in `error.message`, and rendering it under a title that already
        says "Couldn't load announcements" states the same fact twice in slightly
        different words, which reads as a rendering fault rather than as an explanation.
        Seen live on /whats-new with the API stopped.

        So: suppress any message that is itself a load-failure restatement, and show the
        advice line instead — the reader learns nothing from the second sentence, but
        they do learn something from "try again in a moment". A server message that says
        anything else still comes through untouched.
      */
      text={
        (() => {
          const message = (error as Error)?.message?.trim()
          const restatesTitle = !!message && /^(couldn't|could not|unable to)\s+load\b/i.test(message)
          return message && !restatesTitle
            ? message
            : 'Something went wrong on our side. Try again in a moment.'
        })()
      }
      buttons={
        onRetry ? (
          <Button variant="secondary" disableElevation onClick={onRetry}>
            Try again
          </Button>
        ) : undefined
      }
    />
  )
}
